import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Share2, Download, Loader2, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "@/App";
import html2canvas from "html2canvas";
import { toast } from "sonner";

const PHASE_COLORS = {
  menstrual: { bg: "from-red-900/80 to-rose-950/90", accent: "#f43f5e" },
  follicular: { bg: "from-emerald-900/80 to-teal-950/90", accent: "#10b981" },
  ovulation: { bg: "from-amber-900/80 to-orange-950/90", accent: "#f59e0b" },
  luteal: { bg: "from-indigo-900/80 to-purple-950/90", accent: "#8b5cf6" },
  unknown: { bg: "from-slate-900/80 to-gray-950/90", accent: "#a78bfa" },
};

const ShareCard = ({ onClose }) => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const cardRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/share/card-data`, { headers, withCredentials: true });
        setData(res.data);
      } catch (error) {
        // data loads with fallback state
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const downloadCard = async () => {
    if (!cardRef.current) return;
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#0f0518",
        scale: 2,
        useCORS: true,
      });
      const link = document.createElement("a");
      link.download = `sacred-celestial-flow-${new Date().toISOString().split("T")[0]}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Card downloaded!");
    } catch (err) {
      toast.error("Failed to download card");
    }
  };

  const shareCard = async () => {
    if (!cardRef.current) return;
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#0f0518",
        scale: 2,
        useCORS: true,
      });
      canvas.toBlob(async (blob) => {
        if (!blob) {
          downloadCard();
          return;
        }
        const file = new File([blob], "sacred-celestial-flow.png", { type: "image/png" });
        // Web Share API (with files) is blocked in iframes / many browsers.
        // Probe with canShare first; fall back to download if anything throws.
        const canShareFiles =
          typeof navigator.canShare === "function" && navigator.canShare({ files: [file] });
        if (!canShareFiles) {
          downloadCard();
          return;
        }
        try {
          await navigator.share({
            title: "My Cosmic Alignment",
            text: `${data?.moon.emoji} ${data?.moon.phase} - ${data?.cycle.phase_name}`,
            files: [file],
          });
        } catch (err) {
          // User cancelled OR permission denied (e.g. iframe sandbox).
          if (err?.name !== "AbortError") {
            downloadCard();
          }
        }
      });
    } catch (err) {
      downloadCard();
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
      </div>
    );
  }

  if (!data) return null;

  const colors = PHASE_COLORS[data.cycle.phase] || PHASE_COLORS.unknown;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className="max-w-md w-full" onClick={(e) => e.stopPropagation()}
        >
          {/* The shareable card */}
          <div ref={cardRef} className={`bg-gradient-to-br ${colors.bg} rounded-2xl overflow-hidden`} style={{ background: "linear-gradient(135deg, #0f0518 0%, #1a0d2e 50%, #2d1047 100%)" }}>
            <div className="p-6 space-y-5">
              {/* Header */}
              <div className="text-center">
                <p className="text-xs uppercase tracking-[0.3em] text-purple-300/60 mb-1">Sacred Celestial Flow</p>
                <p className="text-purple-200/50 text-xs">{data.date}</p>
              </div>

              {/* Moon Phase - Hero */}
              <div className="text-center py-3">
                <div className="text-6xl mb-2">{data.moon.emoji}</div>
                <h2 className="text-2xl font-bold text-white tracking-wide">{data.moon.phase}</h2>
                <p className="text-purple-300/80 text-sm mt-1">{data.moon.illumination}% illuminated</p>
                <p className="text-amber-300/70 text-xs mt-1">Moon in {data.moon.sign}</p>
              </div>

              {/* Energy & Cycle */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/5 rounded-xl p-3 text-center border border-white/10">
                  <p className="text-xs text-purple-300/60 uppercase tracking-wider mb-1">Moon Energy</p>
                  <p className="text-white text-sm font-medium">{data.moon.energy}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3 text-center border border-white/10">
                  <p className="text-xs text-pink-300/60 uppercase tracking-wider mb-1">Cycle Phase</p>
                  <p className="text-white text-sm font-medium">{data.cycle.phase_name}</p>
                  {data.cycle.day > 0 && <p className="text-purple-200/50 text-xs">Day {data.cycle.day}</p>}
                </div>
              </div>

              {/* Recommendations */}
              <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                <div className="flex justify-between text-xs">
                  <span className="text-purple-300/60">Crystal: <span className="text-white">{data.moon.crystal}</span></span>
                  <span className="text-purple-300/60">Herb: <span className="text-white">{data.moon.herb}</span></span>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-1">
                <div className="flex items-center gap-2">
                  {data.sun_sign && <span className="text-xs bg-purple-500/20 text-purple-200 px-2 py-0.5 rounded-full">{data.sun_sign}</span>}
                  {data.meditation_streak > 0 && <span className="text-xs bg-amber-500/20 text-amber-200 px-2 py-0.5 rounded-full">{data.meditation_streak} day streak</span>}
                </div>
                <p className="text-[10px] text-purple-400/40">sacredcelestialflow.com</p>
              </div>
            </div>
          </div>

          {/* Action buttons (outside the card so they don't appear in screenshot) */}
          <div className="flex gap-3 mt-4 justify-center">
            <Button onClick={shareCard} className="bg-purple-600 hover:bg-purple-500 text-white rounded-full px-6" data-testid="share-card-btn">
              <Share2 className="w-4 h-4 mr-2" /> Share
            </Button>
            <Button onClick={downloadCard} variant="outline" className="border-purple-500/30 text-purple-200 hover:text-white rounded-full px-6" data-testid="download-card-btn">
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
            <Button onClick={onClose} variant="ghost" className="text-purple-300 hover:text-white rounded-full" data-testid="close-card-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ShareCard;
